import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { OperatorAuth } from "../auth.ts";
import type { PortalConfig } from "../config.ts";
import { visibleFailure } from "../stepUp.ts";
import { endStealthSession, readStealthSession, type StealthSessionView } from "./stealth-api.ts";

export function stealthCommandFailure(cause: unknown): string {
  const raw = cause instanceof Error ? cause.message : "";
  if (raw.includes("Transcript is not healthy.")) {
    return "The transcript is not ready. Commands stay off until the recording is healthy.";
  }
  return visibleFailure(cause, "Command was not sent.") ?? "Command was not sent.";
}

export function StealthDocument(props: { config: PortalConfig; auth: OperatorAuth; sessionId: string }) {
  const [session, setSession] = useState<StealthSessionView | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const sessionRef = useRef<StealthSessionView | null>(null);
  const hubRef = useRef<ReturnType<HubConnectionBuilder["build"]> | null>(null);

  useEffect(() => {
    let stopped = false;
    void readStealthSession(props.config.apiBase, () => props.auth.token(), props.sessionId)
      .then(async (opened) => {
        if (stopped) return;
        sessionRef.current = opened;
        setSession(opened);
        const hub = new HubConnectionBuilder()
          .withUrl(opened.signalr.url, { accessTokenFactory: () => opened.signalr.accessToken })
          .configureLogging(LogLevel.Error)
          .build();
        hub.on(opened.signalr.method, (envelope: unknown) => {
          if (!envelope || typeof envelope !== "object") return;
          const message = envelope as Record<string, unknown>;
          if (message.type !== "commandOutput") return;
          const payload = message.payload;
          if (!payload || typeof payload !== "object") return;
          const body = payload as Record<string, unknown>;
          if (body.transcript === "healthy" || body.transcript === "failed") {
            const base = sessionRef.current;
            if (base) {
              const next: StealthSessionView =
                body.transcript === "healthy"
                  ? { ...base, transcriptHealthy: true, state: base.state === "Starting" ? "Active" : base.state }
                  : { ...base, transcriptHealthy: false, state: "Blocked" };
              sessionRef.current = next;
              setSession(next);
            }
            if (body.transcript === "failed") setError("The transcript failed. This shell is closed.");
            return;
          }
          if (typeof body.transcript === "string") return;
          if (typeof body.text !== "string" || body.text.length === 0) return;
          setLines((current) => [...current, body.text as string]);
        });
        hubRef.current = hub;
        await hub.start();
      })
      .catch((cause: unknown) => {
        const message = visibleFailure(cause, "Stealth Command could not open.");
        if (!stopped && message) setError(message);
      });
    return () => {
      stopped = true;
      const hub = hubRef.current;
      hubRef.current = null;
      void hub?.stop();
    };
  }, [props.auth, props.config.apiBase, props.sessionId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const current = sessionRef.current;
    const hub = hubRef.current;
    const text = draft;
    if (!current?.nonce || !hub || text.trim().length === 0) return;
    const next = seq.current + 1;
    setDraft("");
    try {
      await hub.invoke(current.signalr.method, {
        v: 1,
        sessionId: current.sessionId,
        type: "command",
        payload: { nonce: current.nonce, seq: next, text },
      });
      seq.current = next;
    } catch (cause) {
      setDraft(text);
      setError(stealthCommandFailure(cause));
    }
  }

  async function end() {
    try {
      await endStealthSession(props.config.apiBase, () => props.auth.token(), props.sessionId);
      setSession((current) => (current ? { ...current, state: "Ended" } : current));
    } catch (cause) {
      const message = visibleFailure(cause, "Session could not be ended.");
      if (message) setError(message);
    }
  }

  return (
    <section className="panel">
      <header className="panel-head">
        <h1>Stealth Command</h1>
        <p>Silent policy. The person at the device is not asked.</p>
      </header>
      {error ? <p role="alert">{error}</p> : null}
      {session ? (
        <p>
          {session.state}
          {session.recordingRule ? ` · ${session.recordingRule}` : ""}
          {session.transcriptHealthy || session.state === "Blocked" || session.state === "Ended" ? "" : " · transcript pending"}
        </p>
      ) : (
        <p>Opening the shell.</p>
      )}
      <pre className="ps-console" aria-label="Stealth Command console">
        {lines.join("")}
      </pre>
      <form onSubmit={(event) => void submit(event)}>
        <label>
          Command
          <input value={draft} onChange={(event) => setDraft(event.target.value)} autoComplete="off" />
        </label>
        <button type="submit" disabled={session === null || !session.transcriptHealthy || session.state === "Blocked" || session.state === "Ended"}>
          Run
        </button>
        <button type="button" onClick={() => void end()}>
          End session
        </button>
      </form>
    </section>
  );
}
